import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import erpInventoryServices from "app/services/erp-inventory-services";
import warehouseBinServices from "app/services/warehouse-bin-services";

// Vista de alzado: el rack visto de frente. Los niveles van de arriba hacia
// abajo en pantalla igual que en la bodega, con el nivel A (el piso) abajo.
const buildElevation = (bins = []) => {
    const levels = new Map();

    bins.forEach((bin) => {
        const level = bin.level || "—";
        if (!levels.has(level)) levels.set(level, []);
        levels.get(level).push(bin);
    });

    return Array.from(levels.entries())
        .map(([level, levelBins]) => ({
            level,
            bins: levelBins.sort((a, b) => String(a.position || "").localeCompare(String(b.position || ""))),
        }))
        .sort((a, b) => String(b.level).localeCompare(String(a.level)));
};

const RackDrawer = ({ rack, open, onClose, onChanged, canEdit, centreId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [deleting, setDeleting] = useState(false);

    const [selectedBinId, setSelectedBinId] = useState(null);
    const [binDetail, setBinDetail] = useState(null);
    const [loadingBin, setLoadingBin] = useState(false);

    const [skuOptions, setSkuOptions] = useState([]);
    const [skuSearch, setSkuSearch] = useState("");
    const [searching, setSearching] = useState(false);
    const [assigning, setAssigning] = useState(false);

    const elevation = useMemo(() => buildElevation(rack?.bins), [rack]);

    // Al abrir un rack se selecciona su primera posición, para que el panel de
    // abajo no aparezca vacío pidiendo un clic extra.
    useEffect(() => {
        if (!open || !rack?.bins?.length) return;
        setSelectedBinId(rack.bins[0].id);
    }, [open, rack]);

    const loadBin = useCallback(async (binId) => {
        if (!binId) {
            setBinDetail(null);
            return;
        }
        try {
            setLoadingBin(true);
            const data = await warehouseBinServices.getBin(binId);
            setBinDetail(data);
        } catch (_error) {
            setBinDetail(null);
        } finally {
            setLoadingBin(false);
        }
    }, []);

    useEffect(() => {
        loadBin(selectedBinId);
    }, [selectedBinId, loadBin]);

    useEffect(() => {
        if (!skuSearch || skuSearch.length < 2) {
            setSkuOptions([]);
            return undefined;
        }

        // Debounce: el buscador de SKU dispara en cada tecla y el catálogo de un
        // cliente puede ser grande.
        const timer = setTimeout(async () => {
            try {
                setSearching(true);
                // Se busca en el CATÁLOGO (erp_inventories), no en la tabla `skus`:
                // esa es el registro de productos sin código de barra, no el
                // catálogo, y para varias empresas tiene cero filas — el buscador
                // no devolvía nada y la asignación era inusable.
                const data = await erpInventoryServices.getInventories(
                    `?q=${encodeURIComponent(skuSearch)}&centre_id=${centreId}&per_page=20`
                );
                setSkuOptions(data?.data || []);
            } catch (_error) {
                setSkuOptions([]);
            } finally {
                setSearching(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [skuSearch, centreId]);

    // Asignar y quitar van por el mismo endpoint que usa la pantalla de
    // Inventario (erp_inventories/set_bin), que escribe en product_bins. Antes
    // esto escribía en sku_bins, otra tabla para el mismo hecho: las dos pantallas
    // se contradecían y ninguna veía lo de la otra.
    const assignProduct = async (producto) => {
        if (!producto || !binDetail) return;

        try {
            setAssigning(true);
            await erpInventoryServices.setProductBin({
                centre_id: centreId,
                product_code: producto.code,
                bin_id: binDetail.id,
            });
            await loadBin(binDetail.id);
            setSkuSearch("");
            setSkuOptions([]);
            enqueueSnackbar(`${producto.code} asignado a ${binDetail.code}`, { variant: "success" });
            onChanged();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo asignar el producto", { variant: "error" });
        } finally {
            setAssigning(false);
        }
    };

    const unassignProduct = async (code) => {
        try {
            // Sin bin_id ni bin_code, set_bin quita la posición.
            await erpInventoryServices.setProductBin({ centre_id: centreId, product_code: code });
            await loadBin(binDetail.id);
            enqueueSnackbar(`${code} quitado de ${binDetail.code}`, { variant: "success" });
            onChanged();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo quitar el producto", { variant: "error" });
        }
    };

    // Borra el rack completo. Las posiciones con productos encima se dejan y se
    // informan: eliminarlas dejaría esos productos sin ubicación sin avisar.
    const deleteRack = async () => {
        if (!rack?.bins?.length) return;

        try {
            setDeleting(true);
            const result = await warehouseBinServices.bulkDestroy({
                centre_id: centreId,
                ids: rack.bins.map((bin) => bin.id),
            });
            const extra = result.skipped_count > 0
                ? ` (${result.skipped_count} con productos quedaron sin borrar)`
                : "";
            enqueueSnackbar(`${result.message}${extra}`, { variant: "success" });
            onChanged();
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo eliminar", { variant: "error" });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 540 } } }}>
            <Box sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ mb: 0 }}>
                            {rack?.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {rack?.occupied} de {rack?.total} posiciones ocupadas
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseOutlinedIcon />
                    </IconButton>
                </Stack>

                {rack?.stacked && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Esta celda del plano tiene <strong>{rack.rackCount} racks distintos</strong>, así que el alzado
                        de abajo mezcla las posiciones de todos y repite los niveles. Arrastra uno de los racks a otra
                        celda para separarlos.
                    </Alert>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Alzado
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 3 }}>
                    <Stack spacing={1}>
                        {elevation.map((row) => (
                            <Stack key={row.level} direction="row" spacing={1} alignItems="center">
                                <Typography
                                    variant="caption"
                                    sx={{ width: 24, fontWeight: 600, color: "text.secondary", flexShrink: 0 }}
                                >
                                    {row.level}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {row.bins.map((bin) => (
                                        <Tooltip key={bin.id} title={bin.code} arrow>
                                            <Box
                                                onClick={() => setSelectedBinId(bin.id)}
                                                sx={(theme) => ({
                                                    cursor: "pointer",
                                                    minWidth: 56,
                                                    px: 1,
                                                    py: 0.75,
                                                    borderRadius: 1,
                                                    textAlign: "center",
                                                    border: "2px solid",
                                                    borderColor:
                                                        selectedBinId === bin.id ? "primary.main" : "transparent",
                                                    bgcolor:
                                                        bin.sku_count > 0
                                                            ? alpha(theme.palette.primary.main, 0.35)
                                                            : theme.palette.action.hover,
                                                })}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                    {bin.position || bin.code}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {bin.sku_count} SKU
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    ))}
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </Paper>

                <Divider sx={{ mb: 2 }} />

                {loadingBin && (
                    <Stack alignItems="center" sx={{ py: 3 }}>
                        <CircularProgress size={28} />
                    </Stack>
                )}

                {!loadingBin && binDetail && (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="h6" sx={{ mb: 0 }}>
                                {binDetail.code}
                            </Typography>
                            {binDetail.source === "erp" && <Chip size="small" label="Del ERP" color="info" />}
                            {!binDetail.structured && (
                                <Chip size="small" label="Sin estructura" color="warning" />
                            )}
                        </Stack>

                        {binDetail.external_ref && binDetail.external_ref !== binDetail.code && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Texto original del ERP: {binDetail.external_ref}
                            </Typography>
                        )}

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            Productos en esta posición
                        </Typography>

                        {(binDetail.products || []).length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Ninguno todavía.
                            </Typography>
                        )}

                        <Stack spacing={1}>
                            {(binDetail.products || []).map((entry) => (
                                <Paper
                                    key={entry.product_bin_id}
                                    variant="outlined"
                                    sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {entry.code}
                                            </Typography>
                                            {entry.locked && (
                                                <Tooltip title="El ERP no la sobrescribe">
                                                    <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                </Tooltip>
                                            )}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" noWrap
                                                    sx={{ display: "block", maxWidth: 340 }}>
                                            {entry.name}
                                            {entry.stock !== null && entry.stock !== undefined && ` · stock ${entry.stock}`}
                                        </Typography>
                                    </Box>
                                    {canEdit && (
                                        <IconButton size="small" onClick={() => unassignProduct(entry.code)}>
                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Paper>
                            ))}
                        </Stack>

                        {canEdit && (
                            <Box sx={{ mt: 2 }}>
                                <Autocomplete
                                    options={skuOptions}
                                    loading={searching}
                                    getOptionLabel={(option) => (option.code ? `${option.code} — ${option.name || ""}` : "")}
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                    filterOptions={(options) => options}
                                    onInputChange={(_event, value) => setSkuSearch(value)}
                                    onChange={(_event, value) => assignProduct(value)}
                                    noOptionsText={
                                        skuSearch.length < 2 ? "Escribe al menos 2 caracteres" : "Sin resultados"
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            label="Asignar un producto a esta posición"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {(searching || assigning) && <CircularProgress size={18} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                    Las asignaciones hechas acá quedan bloqueadas: la sincronización del ERP no las
                                    sobrescribe.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
                    {canEdit ? (
                        <LoadingButton color="error" size="small" loading={deleting}
                                       startIcon={<DeleteSweepOutlinedIcon />} onClick={deleteRack}>
                            Eliminar este rack
                        </LoadingButton>
                    ) : <Box />}
                    <Button onClick={onClose}>Cerrar</Button>
                </Stack>
            </Box>
        </Drawer>
    );
};

export default RackDrawer;
